import { Component, AfterViewInit, OnDestroy, ViewChild, ElementRef, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// Rxjs
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { WebcomponentsService } from '@core/service/webcomponents.service';
import { AuthService } from '@auth/auth.service';
import { MarketingEventService } from '@shared/services/marketing-events/marketing-events.service';

// Configs
import { HEADER_ROUTES_CONSTANTS } from '@home/header/configs';
import { CHANNELS_ENUMERATION, CHANNELS_EVENTS_ENUMERATION } from '@shared/services/marketing-events/configs';

@Component({
  selector: 'app-payment-status',
  templateUrl: './payment-status.component.html',
  styleUrls: ['./payment-status.component.scss'],
})
export class PaymentStatusComponent implements AfterViewInit, OnDestroy {
  @ViewChild('paymentStatusRef', { static: false }) public paymentStatusRef: ElementRef;
  public HEADER_ROUTES_CONSTANTS: typeof HEADER_ROUTES_CONSTANTS;
  private readonly unSubscribe$: Subject<void>;
  private readonly activateRoute: ActivatedRoute;
  private readonly authService: AuthService;

  private readonly marketingEventService: MarketingEventService;

  constructor(private readonly webComponentService: WebcomponentsService, private readonly inject: Injector) {
    this.HEADER_ROUTES_CONSTANTS = HEADER_ROUTES_CONSTANTS;
    this.unSubscribe$ = new Subject<void>();
    this.activateRoute = this.inject.get(ActivatedRoute);
    this.authService = this.inject.get(AuthService);
    this.marketingEventService = this.inject.get(MarketingEventService);
  }

  public ngAfterViewInit(): void {
    this.notifyPaymentStatusToApp();
    this.renderPaymentStatusWebComponent();
  }

  private notifyPaymentStatusToApp(): void {
    this.marketingEventService.notifyEventToChannel(CHANNELS_ENUMERATION.CLOSE_CHANNEL, {
      event: CHANNELS_EVENTS_ENUMERATION.PAYMENT_STATUS,
      payload: {
        transactionId: this.getTransactionId(),
        isNovation: this.getIsNovation(),
        novationType: this.getNovationTypeId(),
      },
    });
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  private renderPaymentStatusWebComponent(): void {
    const paymentStatusNativeElement = this.paymentStatusRef.nativeElement;
    this.webComponentService.isLoaded$.pipe(takeUntil(this.unSubscribe$)).subscribe({
      next: (loaded) => {
        if (loaded) {
          const isNovation = this.getIsNovation();
          const buildConfig = isNovation
            ? {
                isNovation: true,
                novationType: this.getNovationTypeId(),
              }
            : {
                isNovation: false,
                transactionId: this.getTransactionId(),
              };

          paymentStatusNativeElement.ngElementStrategy?.componentRef?.instance?.build(buildConfig);
        }
      },
    });
  }

  private getIsNovation(): boolean {
    const queryParams = this.activateRoute.snapshot.queryParams;
    return queryParams['isNovation'] !== undefined && queryParams['isNovation'] !== null;
  }

  private getNovationTypeId(): string {
    const queryParams = this.activateRoute.snapshot.queryParams;
    return queryParams['novationTypeId'] || '';
  }

  private getTransactionId(): string {
    const queryParams = this.activateRoute.snapshot.queryParams;
    return queryParams['vads_ext_trans_id'] || queryParams['id'] || '';
  }

  public isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }
}
